import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Users, GraduationCap, BookOpen, ArrowRight, ChevronDown } from "lucide-react";
import { courses, faqs } from "../mock/mock";
import { PageHero } from "./About";

const Courses = () => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div>
      <PageHero
        eyebrow="Programs"
        title="Courses & Curriculum"
        subtitle="NCTE-approved teacher training programs designed for the classrooms of tomorrow."
        image="https://images.unsplash.com/photo-1577896851231-70ef18881754"
      />

      {/* Courses list */}
      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-10">
          {courses.map((c, i) => (
            <article
              key={c.id}
              className="grid lg:grid-cols-12 gap-8 bg-brand-ivory rounded-sm p-8 md:p-10 border border-brand-gold/25 hover-lift animate-fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="lg:col-span-4">
                <div className="text-xs uppercase tracking-[0.3em] text-brand-gold-dark font-bold mb-3">{c.code}</div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-brand-green-deep leading-tight mb-6">{c.title}</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-brand-gold-dark" />
                    <span className="text-neutral-600">Duration:</span>
                    <span className="font-semibold text-brand-green-deep">{c.duration}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-brand-gold-dark" />
                    <span className="text-neutral-600">Seats:</span>
                    <span className="font-semibold text-brand-green-deep">{c.seats}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <GraduationCap className="w-4 h-4 text-brand-gold-dark mt-0.5 shrink-0" />
                    <div>
                      <div className="text-neutral-600">Eligibility:</div>
                      <div className="font-semibold text-brand-green-deep">{c.eligibility}</div>
                    </div>
                  </div>
                </div>
                <Link to="/admissions" className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold">
                  Apply Now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="lg:col-span-8 lg:border-l lg:border-brand-gold/25 lg:pl-8">
                <p className="text-neutral-700 leading-relaxed mb-6">{c.desc}</p>
                <h4 className="font-serif-display text-xl text-brand-green-deep mb-4">Program Highlights</h4>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {c.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-neutral-700">
                      <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Curriculum */}
      <section className="section-y bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Curriculum</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep">What you&rsquo;ll learn</h2>
            <p className="mt-4 max-w-2xl mx-auto text-neutral-600">
              A well-rounded curriculum balancing pedagogical theory, subject content, classroom practice, and modern tools.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: "Semester 1", topics: ["Childhood & Development", "Contemporary Society", "Language 1", "Educational Psychology"] },
              { title: "Semester 2", topics: ["Pedagogy of Mathematics", "Pedagogy of Science", "Language 2", "School Culture"] },
              { title: "Semester 3", topics: ["Pedagogy of Social Studies", "Assessment for Learning", "ICT in Education", "Health & Physical Ed."] },
              { title: "Semester 4", topics: ["School Internship", "Understanding the Self", "Inclusive Education", "Creative Drama & Arts"] },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-sm p-6 border border-brand-gold/25 hover-lift animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center border border-brand-gold">
                    <BookOpen className="w-5 h-5 text-brand-gold" />
                  </div>
                  <h3 className="font-serif-display text-xl text-brand-green-deep">{s.title}</h3>
                </div>
                <ul className="space-y-2 text-sm text-neutral-700">
                  {s.topics.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-1.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">FAQ</div>
            <h2 className="font-serif-display text-4xl font-bold text-brand-green-deep">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="border border-brand-gold/25 rounded-sm bg-brand-ivory overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-serif-display text-lg text-brand-green-deep">{f.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-brand-gold-dark transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${
                      open ? "max-h-60" : "max-h-0"
                    }`}
                  >
                    <div className="px-6 pb-5 text-neutral-700 leading-relaxed text-sm">{f.a}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
